import { isIFrame } from "@/app/utils/IFrameUtils";
import { AUTH_SCOPE, LOGIN_METRICS } from "../constants";
import { submitPreLoginEventBeforeRedirect } from "./metrics-utils";
import { LOGIN_JOURNEY, LOGIN_STAGE_RESULTS, LOGIN_STAGES, StructuredLog, trackClientLoginStage } from "@agentx/agentx-services";
import { getRedirectionUrl } from "@uuip/unified-ui-platform";
import { assert, ensureError } from "@uuip/unified-ui-platform-sdk";
import qs from "qs";
import { getOrCreateWebexInstance, initializeWebexWithConfig } from "../webex/webex-initialization";
import { hashString } from "./hashUtils";
import { getIFrameHelper } from "./iFrameHelper";
import { encodeIFrameLoginState } from "./login-transaction-state";
export const IDENTITY_URLS_KEY = "identityUrls";
export function useWebexAuth() {
    return IS_CDN_BUILD || IS_LOCAL_BUILD || window.isUnderTest === true;
}
function isWebexAuthCodeMessage(data) {
    if (typeof data !== "object" || data === null) {
        return false;
    }
    const message = data;
    return (message.type === "webex-auth-code" &&
        typeof message.code === "string" &&
        message.code.length > 0 &&
        typeof message.transactionId === "string" &&
        message.transactionId.length > 0);
}
export async function fetchCiUrls(webexServices) {
    StructuredLog.trace("Login/EntryUtils/FetchCiUrls");
    await webexServices.waitForCatalog("preauth", 20000);
    const ciUrls = {
        identityUrl: webexServices.get("identity") ?? "",
        idbrokerUrl: webexServices.get("idbroker") ?? ""
    };
    assert(ciUrls.identityUrl, "Identity URL is not available in Webex instance");
    assert(ciUrls.idbrokerUrl, "ID Broker URL is not available in Webex instance");
    StructuredLog.trace("Login/EntryUtils/FetchCiUrls/Done", "", ciUrls);
    localStorage.setItem(IDENTITY_URLS_KEY, JSON.stringify(ciUrls));
    return ciUrls;
}
function generateLoginUrl(idBrokerUrl, emailHash, transactionId) {
    const redirectUri = getRedirectionUrl((defaultRedirectUrl) => {
        StructuredLog.error("Login/EntryUtils/GenerateLoginUrl", "Falling back to window.location.origin");
        return defaultRedirectUrl;
    });
    const query = qs.stringify({
        response_type: "code",
        client_id: CLIENT_ID,
        scope: AUTH_SCOPE,
        redirect_uri: redirectUri,
        emailHash,
        ...(transactionId ? { state: encodeIFrameLoginState(transactionId) } : {})
    });
    return {
        loginUrl: `${idBrokerUrl}/idb/oauth2/v1/authorize?${query}`,
        callbackOrigin: new URL(redirectUri).origin
    };
}
export function getEmailHashFromState(state) {
    try {
        StructuredLog.trace("Login/EntryUtils/TryParseState");
        return JSON.parse(atob(state)).emailhash;
    }
    catch (error) {
        const ensuredError = ensureError(error);
        StructuredLog.error("Login/EntryUtils/ParseState/Error", "Failed to parse login state", {
            error: ensuredError.message
        });
        return undefined;
    }
}
function cleanupPopupLogin(popupLoginState, messageEventListener) {
    if (popupLoginState.checkClosedInterval !== undefined) {
        clearInterval(popupLoginState.checkClosedInterval);
    }
    window.removeEventListener("message", messageEventListener);
}
function terminatePopupLogin(loginWindow, popupLoginState, messageEventListener, onPopupClosedByUser) {
    cleanupPopupLogin(popupLoginState, messageEventListener);
    loginWindow.close();
    onPopupClosedByUser(true);
}
function isTrustedPopupMessage(event, loginWindow, callbackOrigin) {
    if (event.source === loginWindow && event.origin === callbackOrigin) {
        return true;
    }
    StructuredLog.trace("Login/EntryUtils/RedirectToWebexLogin/IgnoreMessage");
    return false;
}
function isValidPopupAuthCodeMessage(data, popupLoginState) {
    return isWebexAuthCodeMessage(data) && data.transactionId === popupLoginState.transactionId;
}
function redirectToAuthorizationCode(code) {
    const callbackUrl = new URL(window.location.origin);
    if (isIFrame()) {
        const iframeUrl = new URL(window.location.href);
        iframeUrl.searchParams.forEach((value, key) => {
            if (key !== "state" && key !== "code") {
                callbackUrl.searchParams.append(key, value);
            }
        });
    }
    callbackUrl.searchParams.set("code", code);
    window.location.replace(callbackUrl.toString());
}
function redirectToWebexLoginInPopup(loginUrl, transactionId, callbackOrigin, onPopupClosedByUser) {
    const loginWindow = window.open(loginUrl, "login", "width=500,height=500");
    if (!loginWindow) {
        StructuredLog.error(`Login/EntryUtils/RedirectToWebexLogin/PopupFailed`, "Failed to open login popup");
        onPopupClosedByUser(false);
        return;
    }
    const popupLoginState = {
        transactionId,
        callbackOrigin
    };
    const messageEventListener = (event) => {
        if (!isTrustedPopupMessage(event, loginWindow, popupLoginState.callbackOrigin)) {
            return;
        }
        const messageData = event.data;
        if (!isValidPopupAuthCodeMessage(messageData, popupLoginState)) {
            terminatePopupLogin(loginWindow, popupLoginState, messageEventListener, onPopupClosedByUser);
            return;
        }
        cleanupPopupLogin(popupLoginState, messageEventListener);
        loginWindow.close();
        redirectToAuthorizationCode(messageData.code);
    };
    popupLoginState.checkClosedInterval = setInterval(() => {
        if (loginWindow.closed) {
            terminatePopupLogin(loginWindow, popupLoginState, messageEventListener, onPopupClosedByUser);
        }
    }, 1000);
    window.addEventListener("message", messageEventListener);
}
async function redirectToWebexLogin(idbrokerUrl, emailHash, onPopupClosedByUser, isEntryFlow) {
    sessionStorage.setItem("loginMode", "loginPage-UUIP");
    const iframeLogin = isIFrame();
    const transactionId = iframeLogin ? crypto.randomUUID() : undefined;
    const { loginUrl, callbackOrigin } = generateLoginUrl(idbrokerUrl, emailHash, transactionId);
    trackClientLoginStage({
        stage: LOGIN_STAGES.IDBROKER,
        result: LOGIN_STAGE_RESULTS.STARTED,
        endpoint: LOGIN_JOURNEY.ENDPOINTS.IDBROKER_AUTHORIZE
    });
    StructuredLog.trace("Login/EntryUtils/RedirectToWebexLogin", "", {
        loginUrl
    });
    // Wait up to PRE_LOGIN_METRICS_TIMEOUT_MS for metrics before redirecting.
    // TSA XHR can hang indefinitely in CI/automation; the timeout ensures login is never blocked.
    await submitPreLoginEventBeforeRedirect(LOGIN_METRICS.REDIRECT_TO_LOGIN, { loginUrl, isEntryFlow }, emailHash);
    // Note: We would prefer to use the webex method initiateLogin here, but it is not compatible with the uuip flows,
    // so for the first version, we're sticking the uuip style redirect to idbroker.
    if (iframeLogin && transactionId) {
        redirectToWebexLoginInPopup(loginUrl, transactionId, callbackOrigin, onPopupClosedByUser);
    }
    else {
        window.location.replace(loginUrl);
    }
}
function removeState() {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.delete("state");
    window.location.replace(currentUrl);
}
async function getWebexOrThrow() {
    const webex = await getOrCreateWebexInstance();
    if (!webex) {
        const errorMessage = "Failed to initialize Webex: Webex instance is null";
        StructuredLog.error("Login/EntryUtils/CreateWebex", errorMessage);
        throw new Error(errorMessage);
    }
    return webex;
}
async function fetchCatalogAndRedirect(webex, emailHash, onLoginError, isEntryFlow) {
    window.webexService = webex;
    StructuredLog.trace("Login/EntryUtils/RedirectToWebexLogin");
    const ciUrls = await fetchCiUrls(webex.internal.services);
    StructuredLog.trace("Login/EntryUtils/RedirectToWebexLogin/FetchedCIUrls", "", { ciUrls });
    await redirectToWebexLogin(ciUrls.idbrokerUrl, emailHash, (wasPopupOpened) => {
        if (wasPopupOpened) {
            StructuredLog.trace("Login/EntryUtils/RedirectToWebexLogin/PopupClosedByUser");
            onLoginError("loginCancelled");
            return;
        }
        StructuredLog.trace("Login/EntryUtils/RedirectToWebexLogin/PopupNotCreated");
        onLoginError("loginPopupNotCreated");
    }, isEntryFlow);
}
export async function fetchCatalogAndRedirectToWebexLogin(webexState, onLoginError, isEntryFlow) {
    StructuredLog.trace("Login/EntryUtils/CreateWebex");
    const emailHash = getEmailHashFromState(webexState);
    if (!emailHash) {
        removeState();
        return;
    }
    try {
        const webex = await getWebexOrThrow();
        await fetchCatalogAndRedirect(webex, emailHash, onLoginError, isEntryFlow);
    }
    catch (error) {
        StructuredLog.error("Login/EntryUtils/RedirectToWebexLogin/Error", "Error while redirecting to Webex login:", {
            error
        });
        onLoginError("loginFailed");
    }
}
async function initiateWebexLogin(authorization, request, cleanupIFrame) {
    if (!authorization) {
        const errorMessage = "Webex authorization is not available";
        StructuredLog.error("Login/EntryUtils/InitiateLogin/Failed", errorMessage);
        cleanupIFrame?.();
        throw new Error(errorMessage);
    }
    try {
        await authorization.initiateLogin(request);
    }
    catch (error) {
        StructuredLog.error("Login/EntryUtils/InitiateLogin/Failed", "Error while initiating login with Webex:", { error });
        cleanupIFrame?.();
        throw error;
    }
}
async function redirectToCI(webex, email, theme, onLoginError) {
    const emailHash = await hashString(email);
    const iFrameHelper = getIFrameHelper(onLoginError);
    const request = {
        emailHash,
        theme,
        state: iFrameHelper ? { transactionId: iFrameHelper.transactionId } : undefined,
        separateWindow: iFrameHelper ? { width: 500, height: 500 } : undefined
    };
    StructuredLog.trace("Login/EntryUtils/LoginWithWebEx/Start");
    await initiateWebexLogin(webex.authorization, request, iFrameHelper
        ? () => {
            iFrameHelper.cleanup();
        }
        : undefined);
    StructuredLog.trace("Login/EntryUtils/LoginWithWebEx/Complete");
}
async function initializeWebexForLogin() {
    const webex = await initializeWebexWithConfig({ userToken: null, useWebExAuth: true });
    if (!webex) {
        const errorMessage = "Failed to initialize Webex: Webex instance is null";
        StructuredLog.error("Login/EntryUtils/LoginWithWebEx", errorMessage);
        throw new Error(errorMessage);
    }
    return webex;
}
async function prepareWebexLogin(webex, email) {
    trackClientLoginStage({
        stage: LOGIN_STAGES.IDBROKER,
        result: LOGIN_STAGE_RESULTS.STARTED,
        endpoint: LOGIN_JOURNEY.ENDPOINTS.IDBROKER_AUTHORIZE
    });
    StructuredLog.trace("Login/EntryUtils/LoginWithWebEx/waitPreAuthCatalog");
    await webex.internal.services.waitForCatalog("preauth");
    StructuredLog.trace("Login/EntryUtils/LoginWithWebEx/collectPreauthCatalog");
    await webex.internal.services.collectPreauthCatalog({ email });
}
export async function loginWithWebex(email, theme, onLoginError) {
    StructuredLog.trace("Login/EntryUtils/LoginWithWebEx");
    sessionStorage.setItem("loginMode", "loginPage-Webex");
    const webex = await initializeWebexForLogin();
    await prepareWebexLogin(webex, email);
    return redirectToCI(webex, email, theme, onLoginError);
}
